import { useEffect, useState } from "react";
import axios from "axios";
import { formatCad } from "@/data/catalog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/keeper/birthdays`;
const FILTERS = [["all", "All"], ["used", "Used"], ["unused", "Waiting"], ["expired", "Expired"], ["failed", "Not delivered"]];
const TONE = { used: "text-[#8FB08A]", unused: "text-[#C9A96B]", expired: "text-[#D3CEC4]/45", failed: "text-[#C9876B]" };
const LABEL = { used: "Used", unused: "Waiting", expired: "Expired", failed: "Not delivered" };
const day = (iso) => (iso ? new Date(iso).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" }) : "—");

const Stat = ({ label, value, testid }) => (
  <div className="border border-[#303B2D]/70 rounded-sm px-5 py-4 min-w-[130px]">
    <p className="text-[10px] uppercase tracking-[0.25em] text-[#D3CEC4]/55">{label}</p>
    <p className="mt-1 font-display text-3xl text-[#E8DDC7]" data-testid={testid}>{value}</p>
  </div>
);

export const KeeperBirthdays = ({ token }) => {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  useEffect(() => {
    axios.get(API, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data)).catch(() => setData({ rows: [], summary: {} }));
  }, [token]);

  if (!data) return <p className="mt-8 text-[#D3CEC4]/50">Opening the birthday book…</p>;
  const s = data.summary || {};
  const rows = (Array.isArray(data.rows) ? data.rows : []).filter((r) => filter === "all" || r.state === filter);

  return (
    <div className="mt-6" data-testid="keeper-birthdays">
      <div className="flex flex-wrap gap-3">
        <Stat label="Letters sent" value={s.sent ?? 0} testid="birthdays-stat-sent" />
        <Stat label="Codes used" value={s.used ?? 0} testid="birthdays-stat-used" />
        <Stat label="Use rate" value={`${s.rate ?? 0}%`} testid="birthdays-stat-rate" />
        <Stat label="Orders from codes" value={formatCad(s.revenue_cents ?? 0)} testid="birthdays-stat-revenue" />
      </div>
      <div className="mt-6 flex flex-wrap gap-2">
        {FILTERS.map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`birthdays-filter-${k}`}
            className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] rounded-sm border transition-colors ${filter === k ? "border-[#B5A642] text-[#B5A642]" : "border-[#303B2D] text-[#D3CEC4]/60 hover:text-[#E8DDC7]"}`}>
            {label}{k !== "all" && ` · ${s[k] ?? 0}`}
          </button>
        ))}
      </div>
      {rows.length === 0 ? (
        <p data-testid="birthdays-empty" className="mt-8 font-display italic text-[#D3CEC4]/55">
          {filter === "all" ? "No birthday letters have gone out yet. They send on each saved dog's birthday once email is switched on." : "Nothing here."}
        </p>
      ) : (
        <div className="mt-6 overflow-x-auto">
          <table className="w-full min-w-[640px] whitespace-nowrap text-left text-[13px] [&_td]:pr-5 [&_th]:pr-5" data-testid="birthdays-table">
            <thead className="text-[10px] uppercase tracking-[0.2em] text-[#D3CEC4]/55">
              <tr><th className="py-2">Dog</th><th>Owner</th><th>Code</th><th>Sent</th><th>Good until</th><th>Status</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.code} className="border-t border-[#303B2D]/60 text-[#D3CEC4]/80" data-testid={`birthday-row-${r.code}`}>
                  <td className="py-2.5 font-display text-[15px] text-[#E8DDC7]">{r.dog_name}</td>
                  <td>{r.email}</td>
                  <td className="font-ledger">{r.code}</td>
                  <td className="font-ledger text-[#D3CEC4]/60">{day(r.sent_at)}</td>
                  <td className="font-ledger text-[#D3CEC4]/60">{day(r.expires_at)}</td>
                  <td className={TONE[r.state]} data-testid={`birthday-state-${r.code}`}>
                    {LABEL[r.state]}
                    {r.state === "used" && <span className="text-[#D3CEC4]/55"> · {day(r.redeemed_at)}{r.order_total_cents != null && ` · ${formatCad(r.order_total_cents)}`}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
